
"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getAuthErrorMessage } from "@/lib/auth-errors";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AUTH_INVALID_EMAIL, AUTH_PASSWORD_REQUIRED } from "@/lib/auth-copy";

const formSchema = z.object({
    newEmail: z.string().email(AUTH_INVALID_EMAIL.description),
    password: z.string().min(1, AUTH_PASSWORD_REQUIRED.description),
});

export function UpdateEmailForm() {
    const { user, reauthenticate, updateUserEmail } = useAuth();
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            newEmail: "",
            password: "",
        },
    });

    async function onSubmit(values: z.infer<typeof formSchema>) {
        setIsLoading(true);
        try {
            await reauthenticate(values.password);
            await updateUserEmail(values.newEmail);
            toast({
                title: "Verification Email Sent",
                description: `Please check your inbox at ${values.newEmail} and click the verification link to complete the email change.`,
            });
            form.reset({ ...values, newEmail: "", password: "" });
        } catch (error: any) {
            console.error("Email update error:", error);
            
            // Get user-friendly error message
            const errorMessage = getAuthErrorMessage(error, 'update');
            
            toast({
                variant: "destructive",
                title: errorMessage.title,
                description: errorMessage.description,
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Update Email</CardTitle>
                <CardDescription>
                    Change the email address associated with your account. You'll receive a verification link at your new email address. 
                    Your current email is: {user?.email}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="newEmail"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>New Email</FormLabel>
                                    <FormControl>
                                        <Input type="email" {...field} autoComplete="off" />
                                    </FormControl>
                                    {form.formState.errors.newEmail && (
                                        <Alert variant="destructive" aria-live="polite">
                                            <AlertTitle>{AUTH_INVALID_EMAIL.title}</AlertTitle>
                                            <AlertDescription>{AUTH_INVALID_EMAIL.description}</AlertDescription>
                                        </Alert>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Confirm with Password</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input 
                                                type={showPassword ? "text" : "password"} 
                                                {...field} 
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={isLoading}
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-4 w-4" />
                                                ) : (
                                                    <Eye className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    {form.formState.errors.password && (
                                        <Alert variant="destructive" aria-live="polite">
                                            <AlertTitle>{AUTH_PASSWORD_REQUIRED.title}</AlertTitle>
                                            <AlertDescription>{AUTH_PASSWORD_REQUIRED.description}</AlertDescription>
                                        </Alert>
                                    )}
                                </FormItem>
                            )}
                        />
                        <Button type="submit" disabled={isLoading}>
                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Update Email
                        </Button>
                    </form>
                </Form>
            </CardContent>
        </Card>
    );
}
